import { useMemo } from "react";
import { ActivitySeries } from "@common/schemas/domains/activity/activity-timeline.schema";
import type { HistoryDailyCount } from "@common/types/auditLog";
import { DensityChart } from "@design-system/DensityChart/DensityChart";
import type {
    DensityChartBar,
    DensityChartHighlight,
    DensityChartSlice,
    DensityChartWindow,
} from "@design-system/DensityChart/DensityChart.lib";
import type { DensityChartBucketUnit } from "@design-system/DensityChart/densityBuckets.lib";
import { UserAvatar } from "@components/avatar/UserAvatar";
import { getUserDisplayName } from "@helpers/user";
import type { TimelinePerson } from "./activityTimelinePeople.lib";
import type { ActivityTimelineSeries } from "./activityTimelineSeries.lib";
import {
    toTimelineDays,
    visibleTimelineSeries,
} from "./activityTimelineSeries.lib";
import styles from "./ActivityTimelineChart.module.scss";

/**
 * A person's segment: their avatar color for the bar (also with a photo),
 * darker while hovered and darker still while highlighted, and their avatar
 * in the tooltip. Avatar colors are free-form pastels around the palette's
 * 200 step, already lighter than the series' 300 fills.
 */
const toPersonSeries = (person: TimelinePerson): ActivityTimelineSeries => ({
    id: `person-${person.id}`,
    series: ActivitySeries.PEOPLE,
    personId: person.id,
    label: getUserDisplayName(person),
    color: person.iconColor,
    // ds-deviation: avatar colors have no 400 shade, so hover and highlight darken the avatar color.
    selectedColor: `color-mix(in oklab, ${person.iconColor}, black 15%)`,
    hoverColor: `color-mix(in oklab, ${person.iconColor}, black 8%)`,
    icon: <UserAvatar user={person} size="xs" aria-hidden />,
});

/** The chart's series, bottom first: the page's series with each person in place of People. */
export const toTimelineSeries = ({
    people,
    series,
}: {
    people: readonly TimelinePerson[];
    series: readonly ActivityTimelineSeries[];
}): readonly ActivityTimelineSeries[] =>
    people.length === 0
        ? series
        : [
              ...people.map(toPersonSeries),
              ...series.filter(
                  (chartSeries) => chartSeries.series !== ActivitySeries.PEOPLE,
              ),
          ];

interface ActivityTimelineChartProps {
    /** Counts rows with `breakdown=series`, or `seriesAndPeople` for `people`, in the chart's bucket. */
    rows: readonly HistoryDailyCount[];
    /** The series charted, bottom first, from `toTimelineSeries`. */
    series: readonly ActivityTimelineSeries[];
    /** History's first day. */
    startDate: string;
    /** Today. */
    endDate: string;
    /** The closest bars wanted; the chart shows coarser ones while the window doesn't fit. */
    bucketUnit: DensityChartBucketUnit;
    /** The unit the bars show, which the rows' bucket follows. */
    onShownBucketUnitChange: (unit: DensityChartBucketUnit) => void;
    window?: DensityChartWindow;
    onWindowChange: (window: DensityChartWindow) => void;
    onWindowPreview?: (window: DensityChartWindow | null) => void;
    /** The days the drawn bars cover, for the page to count. */
    onDrawnDaysChange?: (drawnDays: DensityChartWindow) => void;
    onSliceClick: (slice: DensityChartSlice) => void;
    /** A click on a bar's track off its segments. */
    onBarClick: (bar: DensityChartBar) => void;
    /** The segments drawn in their series' stronger shade. */
    highlight: DensityChartHighlight | undefined;
    /** The runs of days whose counts have loaded. */
    loadedDays: readonly DensityChartWindow[];
    /** The runs of days whose counts failed to load. */
    failedDays: readonly DensityChartWindow[];
    /** The runs of days showing counts kept from another filter while their own load. */
    staleDays: readonly DensityChartWindow[];
    isLoading: boolean;
    /** The sentence on the bars before history starts. */
    beforeStartMessage?: string;
}

/**
 * The Activity timeline: distinct objects each series changed per day, week
 * or month, with the window over it. A slice's tooltip names its series and
 * count. Fetching is the page's, so the workspace and organization pages
 * share it.
 */
export function ActivityTimelineChart({
    rows,
    series,
    startDate,
    endDate,
    bucketUnit,
    onShownBucketUnitChange,
    window,
    onWindowChange,
    onWindowPreview,
    onDrawnDaysChange,
    onSliceClick,
    onBarClick,
    highlight,
    loadedDays,
    failedDays,
    staleDays,
    isLoading,
    beforeStartMessage,
}: ActivityTimelineChartProps) {
    const days = useMemo(() => toTimelineDays(rows, series), [rows, series]);
    const visibleSeries = useMemo(
        () => visibleTimelineSeries(days, series),
        [days, series],
    );

    return (
        <section className={styles.timeline} aria-label="Activity timeline">
            <DensityChart
                ariaLabel="Objects changed over time"
                startDate={startDate}
                endDate={endDate}
                bucketUnit={bucketUnit}
                onShownBucketUnitChange={onShownBucketUnitChange}
                days={days}
                loadedDays={loadedDays}
                failedDays={failedDays}
                series={visibleSeries}
                window={window}
                onWindowChange={onWindowChange}
                onWindowPreview={onWindowPreview}
                onDrawnDaysChange={onDrawnDaysChange}
                onSliceClick={onSliceClick}
                onBarClick={onBarClick}
                highlight={highlight}
                staleDays={staleDays}
                isLoading={isLoading}
                beforeStartMessage={beforeStartMessage}
            />
        </section>
    );
}
